import {
  AddFileParams,
  AddFolderParams,
  AttachmentFileBase,
} from '@nextforce/api'
import { Button, Icon, Input, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { useChatConfig } from 'chat/config/queries/get'
import { useLinkPreview } from 'chat/messages/queries/link_preview'
import * as Clipboard from 'expo-clipboard'
import { Image } from 'expo-image'
import {
  getFriendlyMimeType,
  isImageType,
  isVideoType,
} from 'file_folder_repository/util'
import React, { useCallback, useMemo, useState } from 'react'
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  ScrollView,
  View,
} from 'react-native'
import { AttachmentPreview } from 'shared/components/chat/attachment_preview'
import { NonSelectableText } from 'shared/components/chat/non_selectable_text'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { pickDocument } from 'shared/document_picker'
import {
  convertHeicToJpegIfNeeded,
  pickImageFromLibrary,
} from 'shared/image_picker'
import { getDarkShadeColor, getLightShadeColor } from 'shared/utils'
interface AddFileFolderModalProps {
  isVisible: boolean
  onClose: () => void
  onSubmit: (fields: AddFileParams | AddFolderParams) => Promise<void>
  type: AddFileFolderModalType
  loading?: boolean
}

type UploadType = 'file' | 'photoVideo'

export type AddFileFolderModalType = 'file' | 'folder' | 'url'

export const AddFileFolderModal = ({
  isVisible,
  onClose,
  loading,
  onSubmit,
  type,
}: AddFileFolderModalProps) => {
  const chatConfig = useChatConfig()
  const [fields, setFields] = useState<AddFileParams & AddFolderParams>()
  const [isUploading, setIsUploading] = useState(false)
  const styles = useStyles()

  const disableInputs = loading || (type === 'file' && !fields?.file)

  const onLinkPress = useCallback(async (url: string) => {
    Linking.openURL(url).catch(err => {
      console.error('Error opening link', err)
    })
  }, [])

  const canSubmit = useMemo(() => {
    if (type === 'file') {
      return !!fields?.file && !!fields?.name
    }
    if (type === 'folder') {
      return !!fields?.name
    }
    return !!fields?.externalUrl && !!fields?.name
  }, [fields, type])

  const handleSubmit = async () => {
    setIsUploading(true)
    if (fields) {
      await onSubmit(fields)
      setFields(undefined)
    }
    setIsUploading(false)
  }

  const onPressClose = useCallback(() => {
    setFields(undefined)
    onClose()
  }, [onClose])

  const onUploadButtonPress = useCallback(
    async (uploadType: UploadType) => {
      const result =
        uploadType === 'file'
          ? await pickDocument('*/*', false)
          : await pickImageFromLibrary({
              maxVideoDuration: chatConfig.data?.maxVideoDuration,
            })

      if (result.status === 'success') {
        const picked = result.files[0]
        const converted = await convertHeicToJpegIfNeeded({
          uri: picked.uri,
          mimeType: picked.mimeType,
          name: picked.name,
        })
        const file: AttachmentFileBase = {
          name: converted.name ?? picked.name,
          mimeType: converted.mimeType,
          uri: converted.uri,
        }
        setFields({ ...fields, file, name: file.name })
      }
    },
    [fields, chatConfig.data?.maxVideoDuration]
  )

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        style={styles.overlay}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={styles.modalContainer}>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.titleRow}>
              <ModalIcon type={type} />
              <Text style={styles.title}>
                {type === 'file'
                  ? t('upload')
                  : type === 'folder'
                  ? t('addFolder')
                  : t('addLink')}
              </Text>
            </View>
            {type === 'url' ? (
              <LinkPreviewComponent
                url={fields?.externalUrl}
                onLinkPress={onLinkPress}
              />
            ) : null}
            <View style={styles.inputSectionContainer}>
              {type === 'url' ? (
                <View>
                  <View style={styles.urlInputContainer}>
                    <Input
                      style={styles.urlInput}
                      inputStyle={styles.inputText}
                      containerStyle={styles.inputTextContainer}
                      placeholder={'URL'}
                      cursorColor={styles.inputCursor.color}
                      selectionColor={styles.inputCursor.color}
                      value={fields?.externalUrl}
                      onChangeText={externalUrl =>
                        setFields({ ...fields, externalUrl })
                      }
                    />
                    {!fields?.externalUrl ? (
                      <Icon
                        name="paste"
                        type="font-awesome-5"
                        solid
                        size={24}
                        color={styles.icon.color}
                        containerStyle={styles.pasteUrlIcon}
                        onPress={async () => {
                          const url = await Clipboard.getStringAsync()
                          setFields({ ...fields, externalUrl: url })
                        }}
                      />
                    ) : null}
                  </View>
                  <FileFolderInputs
                    fields={fields}
                    setFields={setFields}
                    disableInputs={disableInputs}
                  />
                </View>
              ) : null}
              {type === 'file' ? (
                <View style={{ flexDirection: 'row' }}>
                  {fields?.file ? (
                    <View style={{ flex: 1, flexDirection: 'row' }}>
                      <View style={[styles.fileIconContainer]}>
                        {isImageType(fields.file.mimeType) ||
                        isVideoType(fields.file.mimeType) ? (
                          <AttachmentPreview
                            attachment={{
                              name: fields.file.name,
                              uri: fields.file.uri,
                              type: 'media',
                              mimeType: fields.file.mimeType,
                            }}
                            onPressRemove={() =>
                              setFields({
                                ...fields,
                                file: undefined,
                                name: undefined,
                              })
                            }
                            isUploading={isUploading}
                            removeAttachmentIconColor={styles.icon.color}
                            containerStyle={styles.selectedFileContainer}
                            imageStyle={styles.selectedFileThumbnail}
                          />
                        ) : (
                          <>
                            <Icon
                              name="file"
                              type="feather"
                              size={40}
                              color={styles.icon.color}
                            />

                            <PressableOpacity
                              onPress={() =>
                                setFields({
                                  ...fields,
                                  file: undefined,
                                  name: undefined,
                                })
                              }
                              style={styles.removeFileIcon}
                            >
                              <Icon
                                name="close"
                                type="ionicons"
                                size={24}
                                color={styles.icon.color}
                              />
                            </PressableOpacity>
                            <Text
                              style={styles.fileInfoText}
                            >{`${getFriendlyMimeType(
                              fields.file.mimeType
                            )} File`}</Text>
                          </>
                        )}
                      </View>
                      <FileFolderInputs
                        fields={fields}
                        setFields={setFields}
                        disableInputs={disableInputs}
                      />
                    </View>
                  ) : (
                    <View style={styles.fileUploadRow}>
                      <PressableOpacity
                        onPress={() => onUploadButtonPress('file')}
                        style={styles.fileUploadRowItem}
                      >
                        <View style={styles.selectFileIconContainer}>
                          <Icon
                            name="file-alt"
                            type="font-awesome-5"
                            solid
                            color={styles.icon.color}
                          />
                          <Text style={styles.uploadButtonText}>
                            {t('selectFile')}
                          </Text>
                        </View>
                      </PressableOpacity>
                      <PressableOpacity
                        onPress={() => onUploadButtonPress('photoVideo')}
                        style={styles.fileUploadRowItem}
                      >
                        <View style={styles.selectFileIconContainer}>
                          <Icon
                            name="photo-video"
                            type="font-awesome-5"
                            solid
                            color={styles.icon.color}
                          />
                          <Text style={styles.uploadButtonText}>
                            {t('selectPhotoVideo')}
                          </Text>
                        </View>
                      </PressableOpacity>
                    </View>
                  )}
                </View>
              ) : null}
              {type === 'folder' ? (
                <FileFolderInputs
                  fields={fields}
                  setFields={setFields}
                  disableInputs={disableInputs}
                />
              ) : null}
            </View>
          </ScrollView>
          <View style={styles.buttonsSectionContainer}>
            <Button
              title={t('cancel')}
              type={'outline'}
              onPress={onPressClose}
              containerStyle={styles.buttonContainer}
              buttonStyle={[styles.button, styles.cancelButton]}
            />
            <Button
              title={t('submit')}
              onPress={handleSubmit}
              buttonStyle={styles.buttonContainer}
              containerStyle={styles.button}
              loading={loading}
              disabled={!canSubmit}
              disabledStyle={styles.disabledSubmitButton}
            />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  )
}

export const LinkPreviewComponent = ({
  url,
  onLinkPress,
}: {
  url: string | undefined
  onLinkPress: (url: string | undefined) => void
}) => {
  const linkPreviewQuery = useLinkPreview(url)

  const styles = useStyles()
  if (!url) {
    return (
      <View style={styles.linkPreviewContainer}>
        <Text style={styles.linkPreviewText}>Enter URL for Link Preview</Text>
      </View>
    )
  }
  const data = linkPreviewQuery.data

  if (linkPreviewQuery.isLoading && !data) {
    return (
      <View style={styles.linkPreviewContainer}>
        <ActivityIndicator color={styles.icon.color} />
      </View>
    )
  }

  if (!data) {
    return (
      <View style={styles.linkPreviewContainer}>
        <Text style={styles.linkPreviewText}>Can't load preview</Text>
      </View>
    )
  }

  return (
    <PressableOpacity
      onPress={() => onLinkPress(linkPreviewQuery.data?.url)}
      style={styles.linkPreviewContainer}
    >
      {data.image ? <LinkPreviewImage url={data?.image} /> : null}
      <View style={styles.linkDescriptionContainer}>
        {data.title ? (
          <NonSelectableText numberOfLines={2} style={styles.linkPreviewTitle}>
            {data.title}
          </NonSelectableText>
        ) : null}
        {data.description ? (
          <NonSelectableText numberOfLines={2} style={styles.description}>
            {data.description}
          </NonSelectableText>
        ) : null}
        {data.domain ? (
          <NonSelectableText numberOfLines={1} style={styles.domain}>
            {data.domain}
          </NonSelectableText>
        ) : null}
      </View>
    </PressableOpacity>
  )
}

const FileFolderInputs = ({
  fields,
  setFields,
  disableInputs,
}: {
  fields: (AddFileParams & AddFolderParams) | undefined
  setFields: (fields: AddFileParams & AddFolderParams) => void
  disableInputs: boolean
}) => {
  const styles = useStyles()
  return (
    <View style={styles.textInputSectionContainer}>
      <Input
        style={styles.input}
        inputStyle={styles.inputText}
        containerStyle={styles.inputTextContainer}
        cursorColor={styles.inputCursor.color}
        selectionColor={styles.inputCursor.color}
        placeholder={t('title')}
        value={fields?.name}
        onChangeText={name => setFields({ ...fields, name })}
        disabled={disableInputs}
      />
      <Input
        style={[styles.input, styles.descriptionInput]}
        inputStyle={styles.inputText}
        cursorColor={styles.inputCursor.color}
        selectionColor={styles.inputCursor.color}
        containerStyle={styles.inputTextContainer}
        placeholder={t('description')}
        value={fields?.description}
        multiline
        onChangeText={description => setFields({ ...fields, description })}
        disabled={disableInputs}
      />
    </View>
  )
}

const LinkPreviewImage = ({ url }: { url: string }) => {
  const styles = useStyles()

  return (
    <Image
      source={{ uri: url }}
      style={[styles.linkPreviewImage]}
      contentFit="cover"
    />
  )
}

const ModalIcon = ({ type }: { type: AddFileFolderModalType }) => {
  const styles = useStyles()
  const name = type === 'file' ? 'file' : type === 'folder' ? 'folder' : 'link'
  return (
    <Icon
      name={name}
      type="feather"
      size={24}
      color={styles.icon.color}
      style={styles.icon}
    />
  )
}

const useStyles = makeStyles(theme => ({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: getDarkShadeColor(0.5),
  },
  scrollContent: {
    flexGrow: 1,
  },
  scroll: {
    flexShrink: 1,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 420,
    maxHeight: '85%',
    padding: 20,
    backgroundColor: theme.colors.grey7,
    borderRadius: 10,
  },
  titleRow: { flexDirection: 'row' },
  title: {
    fontSize: 18,
    marginBottom: 10,
  },
  input: {
    minHeight: 40,
    maxHeight: 150,
    backgroundColor: theme.colors.white,
    borderRadius: 5,
    paddingHorizontal: 10,
    marginHorizontal: 0,
    marginBottom: 5,
  },
  urlInputContainer: {
    minHeight: 40,
    maxHeight: 150,
    backgroundColor: theme.colors.white,
    borderRadius: 5,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  urlInput: {
    paddingHorizontal: 10,
  },
  inputCursor: {
    color: theme.colors.primary,
  },
  buttonsSectionContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 5,
  },
  icon: {
    marginRight: 10,
    color: theme.colors.primary,
  },
  inputSectionContainer: { marginVertical: 10 },
  inputText: { fontSize: 16 },
  inputTextContainer: { paddingHorizontal: 0 },
  buttonContainer: { borderRadius: 20 },
  button: { borderRadius: 20, width: 100 },
  cancelButton: { borderWidth: 0 },
  uploadButtonText: { textAlign: 'center', marginTop: 10 },
  fileIconContainer: {
    height: 100,
    width: 100,
    borderWidth: 1,
    marginRight: 10,
    borderColor: theme.colors.primary,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectFileIconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    marginHorizontal: 10,
    borderColor: theme.colors.primary,
    borderRadius: 15,
    height: 100,
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor() : getLightShadeColor(),
  },
  fileInfoText: {
    fontSize: 14,
    color: theme.colors.black,
    marginTop: 5,
  },
  externalFileRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedFileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 10,
    padding: 3,
  },
  selectedFileThumbnail: {
    height: 101,
    width: 101,
    borderRadius: 15,
  },
  removeFileIcon: {
    position: 'absolute',
    top: 3,
    right: 3,
  },
  selectedFileDetailsContainer: { flex: 1 },
  descriptionInput: {
    paddingVertical: 10,
    minHeight: 60,
    maxHeight: 100,
  },
  disabledSubmitButton: {
    backgroundColor: theme.colors.white,
  },
  linkPreviewContainer: {
    height: 80,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: getDarkShadeColor(),
    borderRadius: 10,
    flexDirection: 'row',
  },
  linkDescriptionContainer: {
    padding: 5,
    flex: 1,
  },
  linkPreviewTitle: {
    fontSize: 16,
  },
  description: {
    opacity: 0.7,
    fontSize: 14,
  },
  domain: {
    opacity: 0.5,
    fontSize: 14,
  },
  linkPreviewImage: {
    width: 80,
    height: 80,
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
  },
  linkPreviewText: {
    color: theme.colors.grey3,
  },
  pasteUrlIcon: { position: 'absolute', right: 10 },
  fileUploadRow: {
    flex: 1,
    flexDirection: 'row',
  },
  fileUploadRowItem: { flex: 1 },
  textInputSectionContainer: { flex: 1 },
}))
